import { useEffect, useState } from "react";

/**
 * OfficeGallery — a slow, editorial rotating hero of sepia office details.
 *
 * Crossfades between images every ~7 seconds with a very subtle Ken Burns
 * scale-drift. Ivory letterbox, italic caption underneath.
 */
const SLIDES = [
  { src: "/img/office-detail-library.jpg",       caption: "The library — Texas Reports, Southwestern, and the weight of precedent." },
  { src: "/img/office-detail-brass.jpg",         caption: "Brass, marble, and a fountain pen — the tools of quiet counsel." },
  { src: "/img/office-detail-marble.jpg",        caption: "The Carrara conference table — negotiations by daylight." },
  { src: "/img/office-detail-star-window.jpg",   caption: "The star window — light in the shape of Texas." },
  { src: "/img/office-detail-portrait-hall.jpg", caption: "The corridor of jurists — a lineage of restraint." },
  { src: "/img/office-detail-desk.jpg",          caption: "The partner's desk at dusk — where the day's work is set down." },
];

export default function OfficeGallery({ interval = 7000 }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;
    const t = window.setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), interval);
    return () => window.clearInterval(t);
  }, [interval]);

  return (
    <section
      className="bg-ivory py-24 md:py-32 hairline-bottom"
      data-testid="office-gallery-section"
      aria-label="Inside the firm — office gallery"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-baseline justify-between gap-6 mb-10">
          <div>
            <div className="kicker">Inside the Firm</div>
            <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
              A quiet place<br/>
              <span className="italic font-cormorant text-oxblood">to think clearly.</span>
            </h2>
          </div>
          <p className="hidden md:block max-w-sm font-cormorant italic text-slate2 text-lg leading-snug">
            Rooms built for conversation — walnut, brass, and the north-facing light of a Frisco afternoon.
          </p>
        </div>

        {/* Frame */}
        <div
          className="relative w-full overflow-hidden bg-navy/5"
          style={{ aspectRatio: "3 / 2" }}
          data-testid="office-gallery-frame"
        >
          {SLIDES.map((s, i) => (
            <img
              key={s.src}
              src={s.src}
              alt=""
              loading={i === 0 ? "eager" : "lazy"}
              className={`gallery-slide absolute inset-0 h-full w-full object-cover ${
                i === idx ? "gallery-slide-active" : ""
              }`}
              data-testid={`office-slide-${i}`}
              aria-hidden={i !== idx}
            />
          ))}

          {/* Overlay star index */}
          <div
            className="absolute bottom-6 right-6 flex items-center gap-2 px-4 py-2 bg-ivory/85 backdrop-blur-sm"
            data-testid="office-gallery-counter"
          >
            <span className="text-[9px] uppercase tracking-[0.32em] text-brass font-medium">
              {String(idx + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* Caption */}
        <div className="mt-6 flex items-start justify-between gap-8">
          <p
            key={idx}
            className="font-cormorant italic text-lg md:text-xl text-slate2 leading-snug caption-fade"
            data-testid="office-caption"
          >
            {SLIDES[idx].caption}
          </p>
          <div className="hidden md:flex items-center gap-2 shrink-0 pt-2">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-px transition-all duration-500 ${
                  i === idx ? "w-10 bg-brass" : "w-6 bg-brass/30 hover:bg-brass/60"
                }`}
                data-testid={`office-gallery-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .gallery-slide {
          opacity: 0;
          transform: scale(1.02);
          transition: opacity 1500ms ease-in-out, transform 8000ms linear;
          filter: sepia(0.18) contrast(1.02);
        }
        .gallery-slide-active {
          opacity: 1;
          transform: scale(1.08);
        }
        @keyframes lsc-caption-fade {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .caption-fade { animation: lsc-caption-fade 900ms ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .gallery-slide, .caption-fade { transition: none; animation: none; }
        }
      `}</style>
    </section>
  );
}
